import {Card, showNotification} from 'platform/components';
import {Box} from 'platform/foundation';
import {match} from 'ts-pattern';

import {useCallback} from 'react';

import {head, isNil, mergeAll} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {
  usePatchArticleDefaultSupplierMutation,
  usePatchArticleInfoMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {DmsDataGrid, catchUnhandledDataGridActions, logError} from '@dms/shared';
import {CatalogFilter} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {suffixTestId, TestIdProps} from 'shared/web';

import type {ActionCallback, EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

interface OverviewCatalogsInformationTabProps extends TestIdProps {
  maximization?: EagGridMaximization;
  catalogFilter: CatalogFilter;
}

const PREDEFINED_COLUMNS = ['name', 'supplierId'] as const;

export function CatalogsInformationTab(props: OverviewCatalogsInformationTabProps) {
  const {articleId} = useWarehouseParams();

  const [patchArticleInfo] = usePatchArticleInfoMutation();
  const [setDefaultSupplier] = usePatchArticleDefaultSupplierMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    getColumnData,
    refreshData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    const name = getColumnData<string>('name')?.value;
    const supplierId = getColumnData<string>('supplierId')?.value;

    if (isNil(id)) {
      return logError('Row id is not defined');
    }

    if (isNil(name)) {
      return logError('Name is not defined');
    }

    if (isNil(supplierId)) {
      return logError('Supplier id is not defined');
    }

    match(actionKey)
      .with('updateArticle', () => {
        patchArticleInfo({articleId, body: {name}})
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('entity.warehouse.notifications.articleInfoUpdated'))
          )
          .then(() => refreshData());
      })
      .with('setAsDefault', () => {
        setDefaultSupplier({articleId, body: {supplierId}})
          .unwrap()
          .then(() =>
            showNotification.success(i18n.t('entity.warehouse.notifications.defaultSupplierSet'))
          )
          .then(() => refreshData());
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const queryModifier = useCallback(
    (filter: QueryFilterObject) =>
      mergeAll([
        filter,
        {
          branchId: props.catalogFilter.branchId,
          supplierId: props.catalogFilter.supplierId,
          manufacturerId: props.catalogFilter.manufacturerId,
          oemCode: props.catalogFilter.manufacturerNumber,
        },
      ]),
    [
      props.catalogFilter.branchId,
      props.catalogFilter.supplierId,
      props.catalogFilter.manufacturerId,
      props.catalogFilter.manufacturerNumber,
    ]
  );

  return (
    <Card isFullHeight>
      <Box height="100%">
        <DmsDataGrid
          maximization={props.maximization}
          gridCode="warehouse-article-catalogs-info"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          queryModifier={queryModifier}
          data-testid={suffixTestId('datagrid', props)}
        />
      </Box>
    </Card>
  );
}
